/**
 * MobileTabBar — fixed bottom navigation for mobile (X.6.2 chrome; five-slot
 * cap + More sheet added in the nav rework 2026-07-13).
 *
 * The bar shows at most FIVE equal-width slots (the iOS convention) drawn from
 * the gated primary-nav list in founder-priority order (Home, Clients,
 * Directory, Writing, …). When a role can see more than five, the last slot
 * becomes "More" — the first four items keep bar slots and the tail moves into
 * a bottom sheet the "More" tab opens. Equal-width slots + truncated labels
 * mean labels never collide or bleed off-screen (the bug this fixed: an
 * unbounded bar rendered every item, colliding "WorkspaceDashboardInvoices" and
 * pushing the tail past the right edge).
 *
 * The active tab shows the forest-pill treatment behind its icon; the "More"
 * tab is active whenever the current route lives in the sheet. Gating is the
 * caller-independent mobileTabBarLinks(whoami): visibleNavLinks with any
 * entry marked mobileTabBar: false removed, so a role-hidden item never
 * appears in the bar OR the sheet, and neither does an entry kept off the
 * phone bar by design (My time). AppShell renders this ONLY when
 * useIsMobile() is true, and hides the desktop PrimaryNav in that case
 * (mutually exclusive).
 *
 * The sheet is the BottomSheet primitive (dialog semantics, ESC + backdrop
 * close, initial focus, body scroll lock) — PR #529 review swapped out the
 * hand-rolled panel. It also closes on ANY pathname change, so browser-back
 * (which bypasses the items' onClick) never leaves it hanging open.
 *
 * House rule (React #310): every hook runs before any conditional return.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { mobileTabBarLinks, activeNavSection, type NavEntry } from '@/lib/navSection';
import { computeMobileVisibleCount, splitNavLinks } from '@/lib/navOverflow';
import { BottomSheet } from '@/components/primitives/BottomSheet';

/** iOS-convention slot cap (four items + a "More" slot when there are more). */
const MOBILE_MAX_SLOTS = 5;

function tabClass(isActive: boolean): string {
  return (
    'flex-1 min-w-0 flex flex-col items-center gap-1 py-1.5 rounded-[12px] text-[0.6875rem] font-semibold ' +
    'transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
    (isActive ? 'text-sage-800' : 'text-sage-400')
  );
}

function pillClass(isActive: boolean): string {
  return (
    'flex items-center justify-center w-[2.875rem] h-[1.875rem] rounded-pill ' +
    'transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none ' +
    (isActive ? 'bg-sage-800 text-cream' : '')
  );
}

export function MobileTabBar(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const { pathname } = useLocation();
  const active = activeNavSection(pathname);
  const [moreOpen, setMoreOpen] = useState(false);

  // Close the sheet on ANY route change — browser-back / programmatic
  // navigation bypasses the items' onClick, and a sheet left open over a new
  // page is a stuck modal. (ESC + backdrop close live in BottomSheet.)
  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  const links = mobileTabBarLinks(whoami);
  const { visible, overflow } = splitNavLinks(
    links,
    computeMobileVisibleCount(links.length, MOBILE_MAX_SLOTS),
  );
  const overflowActive = overflow.some((l) => l.section === active);

  function renderTab(link: NavEntry): ReactNode {
    const Icon = link.icon;
    const isActive = active === link.section;
    return (
      <Link
        key={link.section}
        to={`/t/${tenantSlug}${link.suffix}`}
        aria-current={isActive ? 'page' : undefined}
        data-testid={`mobile-tab-${link.section}`}
        className={tabClass(isActive)}
      >
        <span className={pillClass(isActive)}>
          <Icon size={19} aria-hidden="true" />
        </span>
        <span className="max-w-full truncate">{link.label}</span>
      </Link>
    );
  }

  return (
    <>
      <nav
        aria-label="Primary"
        data-testid="mobile-tab-bar"
        className="fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around bg-cream border-t border-sage-800/[8%] px-3 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]"
      >
        {visible.map(renderTab)}

        {overflow.length > 0 && (
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            aria-label="More navigation"
            data-testid="mobile-tab-more"
            className={tabClass(overflowActive || moreOpen)}
          >
            <span className={pillClass(overflowActive || moreOpen)}>
              <MoreHorizontal size={19} aria-hidden="true" />
            </span>
            <span className="max-w-full truncate">More</span>
          </button>
        )}
      </nav>

      {/* The More sheet — BottomSheet gives dialog semantics, ESC + backdrop
          close, initial focus, and the body scroll lock. */}
      <BottomSheet
        isOpen={moreOpen && overflow.length > 0}
        onClose={() => setMoreOpen(false)}
        title="More"
      >
        <div
          role="menu"
          aria-label="More navigation"
          data-testid="mobile-more-sheet"
          className="pb-[env(safe-area-inset-bottom)]"
        >
          {overflow.map((link) => {
            const Icon = link.icon;
            const isActive = active === link.section;
            return (
              <Link
                key={link.section}
                role="menuitem"
                to={`/t/${tenantSlug}${link.suffix}`}
                onClick={() => setMoreOpen(false)}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`mobile-more-${link.section}`}
                className={
                  'flex items-center gap-3 px-5 py-3 text-sm transition-colors hover:bg-sand ' +
                  (isActive ? 'font-semibold text-sage-800' : 'text-ink')
                }
              >
                <Icon size={19} aria-hidden="true" />
                {link.label}
              </Link>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
}
